import { useState } from 'react';
import ToggleLicensesButton from '../salesDemo/ToggleLicensesButton';
import {
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DataGrid, GridRowParams } from '@mui/x-data-grid';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router';
import SystemInfoWidgets from './SystemInfoWidgets';
import AnalyzerFilters from './AnalyzerFilters';
import SwitchesFilters from './SwitchesFilters';
import LicenseUploader from './LicenseUploader';
import { SystemPageFilters } from './types';
import React from 'react';
import useSetupTable from './hooks/useSetupTable';
import useSetupSwitchesTable from './hooks/useSetupSwitchesTable';
import useSetupWeatherTable from './hooks/useSetupWeatherTable';

import WeatherFilter from './WeatherFilters';
import { useForm } from 'react-hook-form';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';
import { Box } from '@mui/system';
import ExperimentalButton from './ExperimentalButton';

const initialFormState: SystemPageFilters = {
  analyzerName: '',
  analyzerSerial: '',
  module: null,
  switchName: '',
  analyzerSwitchName: '',
  location: '',
  dish: '',
};

const SystemPage = () => {
  const navigate = useNavigate();
  const [activeDisplay, setActiveDisplay] = useState<
    'analyzers' | 'switches' | 'weather'
  >('analyzers');
  const { control, reset, watch } = useForm<SystemPageFilters>({
    defaultValues: initialFormState,
  });

  const { data: modules } = useSystemModules({});

  const { rows, columns } = useSetupTable(
    watch('analyzerName'),
    watch('analyzerSerial'),
    watch('module')
  );
  const { rows: switchRows, columns: switchColumns } = useSetupSwitchesTable(
    watch('switchName'),
    watch('analyzerSwitchName')
  );
  const { rows: weatherRows, columns: weatherColumns } = useSetupWeatherTable(
    watch('location'),
    watch('dish')
  );

  const rowsSelector = {
    analyzers: rows || [],
    switches: switchRows,
    weather: weatherRows,
  };

  const columnSelector = {
    analyzers: columns,
    switches: switchColumns,
    weather: weatherColumns,
  };

  // Row clicks lead to analyzer pages
  const handleRowClick = (params: GridRowParams) => {
    if (activeDisplay === 'analyzers') navigate(`/analyzers/${params.row.id}`);
  };

  const handleToggle = (
    event: React.MouseEvent,
    value: 'analyzers' | 'switches' | 'weather'
  ) => {
    if (value) setActiveDisplay(value);
    reset(initialFormState);
  };

  return (
    <Grid container spacing={2} direction='column'>
      <SystemInfoWidgets />
      <Grid item container spacing={2} alignItems='center'>
        <Grid item>
          <ToggleButtonGroup
            size='large'
            color='primary'
            exclusive
            onChange={handleToggle}
            value={activeDisplay}
          >
            <ToggleButton value='analyzers'>Analyzers</ToggleButton>
            {modules?.SWITCHES && (
              <ToggleButton value='switches'>Switches</ToggleButton>
            )}
            {modules?.WEATHER && (
              <ToggleButton value='weather'>Weather</ToggleButton>
            )}
          </ToggleButtonGroup>
        </Grid>
        {activeDisplay === 'analyzers' && (
          <>
            <AnalyzerFilters
              control={control}
              clearFilters={() => reset(initialFormState)}
            />
            <LicenseUploader />
            <Grid item>
              <ToggleLicensesButton />
            </Grid>
          </>
        )}
        {activeDisplay === 'switches' && (
          <SwitchesFilters
            control={control}
            clearFilters={() => reset(initialFormState)}
          />
        )}
        {activeDisplay === 'weather' && (
          <WeatherFilter
            control={control}
            clearFilters={() => reset(initialFormState)}
          />
        )}
      </Grid>
      <Grid item>
        <Box
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: '10px 10px 0px 0px',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: '0px 0px 10px 10px',
            },
            '& .tableRow': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-root': {
              borderRadius: '10px',
            },
          }}
        >
          <DataGrid
            rows={rowsSelector[activeDisplay]}
            columns={columnSelector[activeDisplay]}
            disableSelectionOnClick
            autoHeight
            onRowClick={handleRowClick}
            getRowClassName={() => 'tableRow'}
          ></DataGrid>
        </Box>
      </Grid>
      {modules?.WEATHER && (
        <Grid
          item
          container
          justifyContent='flex-end'
          style={{ width: '100%' }}
        >
          <Grid item>
            <Typography variant='body2' align='right'>
              Weather Data From{' '}
              <Link href='https://www.weatherapi.com/'>WeatherAPI.com</Link>
            </Typography>
          </Grid>
        </Grid>
      )}
      <ExperimentalButton />
    </Grid>
  );
};

export default SystemPage;
